import React, { Suspense } from "react";
import { Route, matchPath, useLocation } from "react-router";
import { ProtectedOutlet, SuspenseOutlet } from "./RouteWrapper";
import { withParams } from "./withParams";
import * as Lazy from "../lazyRoutes";

/**
 * Route fragments, mounted by the tree in ../index.jsx.
 *
 * Paths are relative to their mount point, and access control is expressed by
 * nesting under <ProtectedOutlet /> rather than a per-route flag.
 *
 * Trailing "/*" marks routes that matched by prefix under react-router 5, so
 * deeper URLs keep resolving to the same page.
 */

export const eventRoutes = (
  <Route path="events">
    {/* An event page stays reachable by people who aren't members yet. */}
    <Route path=":e_id/*" element={withParams(Lazy.HubEvent)} />

    <Route element={<ProtectedOutlet />}>
      <Route index element={withParams(Lazy.MyEvents)} />
      <Route path=":e_id/select-project" element={withParams(Lazy.HubEvent)} />
      <Route path=":e_id/welcome-judge" element={withParams(Lazy.HubEvent)} />
      <Route path=":e_id/welcome-resource" element={withParams(Lazy.HubEvent)} />
      <Route path=":e_id/scoring/:r_id" element={withParams(Lazy.ScoringContainer)} />
    </Route>
  </Route>
);

export const projectRoutes = (
  <Route path="project">
    <Route path=":p_id" element={withParams(Lazy.ProjectProfile)} />

    <Route element={<ProtectedOutlet />}>
      <Route path="create-project/*" element={withParams(Lazy.CreateProject)} />
      <Route path=":p_id/settings" element={withParams(Lazy.ProjectSettings)} />
      <Route path=":p_id/builders" element={withParams(Lazy.MyProjectBuilders)} />
      <Route path=":p_id/builders/:cb_id/intro" element={withParams(Lazy.BuilderIntro)} />
      <Route path=":p_id/builders/:cb_id/overview" element={withParams(Lazy.BuilderOverview)} />
      <Route
        path=":p_id/builders/:cb_id/topics/:tb_id/:ts_type/:ts_id"
        element={withParams(Lazy.BuilderTopic)}
      />
      <Route
        path=":p_id/builders/:cb_id/topics/:tb_id/:ts_type"
        element={withParams(Lazy.BuilderTopic)}
      />
      <Route path=":p_id/builders/:cb_id/topics/:tb_id" element={withParams(Lazy.BuilderTopic)} />
      <Route path=":p_id/builders/:c_id/:t_id" element={withParams(Lazy.BuilderTopic)} />
    </Route>
  </Route>
);

/**
 * Modals that render over whichever hub page is already matched. Under
 * react-router 5 these were <Route>s sitting outside the <Switch>; matching the
 * location directly keeps that overlay behaviour without the page underneath
 * having to host an <Outlet />.
 *
 * Most specific pattern first — matchPath is exact unless the path ends in "*".
 */
const HUB_MODALS = [
  { path: "/hubs/:h_id/members/:u_id/edit", Component: Lazy.EditHubMembership },
  { path: "/hubs/:h_id/members/:u_id", Component: Lazy.HubMember },
  { path: "/hubs/:h_id/welcome", Component: Lazy.HubJoinModal },
];

/**
 * The same patterns, paths only. These modals render outside the route tree, so
 * react-router's `state.matches` resolves them to the splat underneath
 * (`/hubs/:h_id/members/*`) and never sees `:u_id`. Analytics re-matches this
 * list to recover the real pattern — exported rather than copied so the two
 * cannot drift. Order matters: most specific first.
 */
export const HUB_MODAL_PATHS = HUB_MODALS.map(({ path }) => path);

export const HubModals = () => {
  const { pathname } = useLocation();

  for (const { path, Component } of HUB_MODALS) {
    const match = matchPath(path, pathname);
    if (!match) continue;

    return (
      <Suspense fallback={null}>
        <Component params={match.params} />
      </Suspense>
    );
  }

  return null;
};

const HubsLayout = () => (
  <>
    <SuspenseOutlet />
    <HubModals />
  </>
);

export const hubRoutes = (
  <Route path="hubs" element={<ProtectedOutlet />}>
    <Route element={<HubsLayout />}>
      <Route path=":h_id/library/:builder_id/builders" element={withParams(Lazy.HubLibraryLayout)} />
      <Route path=":h_id/library/*" element={withParams(Lazy.HubLibraryLayout)} />
      <Route path=":h_id/members/*" element={withParams(Lazy.HubMembers)} />
      <Route path=":h_id/manage-members/*" element={withParams(Lazy.HubManageMembers)} />
      <Route path=":h_id/settings" element={withParams(Lazy.HubSettings)} />
      <Route path=":h_id/brain/*" element={withParams(Lazy.BrainLayout)} />
      <Route path=":h_id/groups" element={withParams(Lazy.HubGroups)} />
      <Route path=":h_id/groups/:g_id" element={withParams(Lazy.HubGroupLayout)} />
      <Route path=":h_id/groups/:g_id/schedule" element={withParams(Lazy.GroupBuilderSchedulePage)} />
      <Route path=":h_id/events/new" element={withParams(Lazy.CreateHubEventPage)} />
      <Route path=":h_id/events/*" element={withParams(Lazy.HubEvents)} />
      {/* HubActivity is a function component now and reads useParams() itself. */}
      <Route path=":h_id/activity" element={<Lazy.HubActivity />} />
      <Route path=":h_id/activities/:activity_id" element={withParams(Lazy.SingleActivity)} />
      {/* Anything else under a hub lands on its activity feed. */}
      <Route path=":h_id/*" element={<Lazy.HubActivity />} />
    </Route>
  </Route>
);
